import {
  Box,
  useTheme,
  CircularProgress,
  SxProps,
  Theme,
  TextField,
} from "@mui/material";
import { useAddTaskForm } from "@/modules/tasks/hooks/add-task.hook";
import { Controller, useWatch } from "react-hook-form";
import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import CancelButton from "@/modules/tasks/components/Add/cancel-button";
import { NwButton } from "@nawy/ui";
import TaskFormFields from "@/modules/tasks/components/Add/task-form-fields";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { useState, useEffect, useContext } from "react";
import { useTaskAiEvaluation } from "@/modules/common/hooks/task-ai-evaluation.hook";
import TaskAiEvaluationDisplay from "@/modules/common/components/task-ai-evaluation-display";
import {
  TaskAiEvaluationResponse,
  ToastType,
  PermissionsEnum,
  PermissionLevel,
} from "@/modules/common/types";
import { showToast, userHasPermission } from "@/modules/common/utils";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import PlusIcon from "@/modules/common/components/ui-kit/icons/plus-icon";
import { buildTaskContentWithContext } from "@/modules/tasks/utils";

interface AddNewTaskProps {
  addTaskButtonSX?: SxProps<Theme>;
  disableRipple?: boolean;
  prefilledAssignee?: {
    uid: string;
    name: string;
  };
  prefilledLead?: {
    uid: string;
    nanoId: string;
    name: string;
  };
  prefilledProperty?: {
    uid: string;
  };
  disableAssignee?: boolean;
  disableLead?: boolean;
  disableProperty?: boolean;
  disabled?: boolean;
}

const AddNewTask = ({
  disableRipple,
  prefilledAssignee,
  prefilledLead,
  prefilledProperty,
  disableAssignee = false,
  disableLead = false,
  disableProperty = false,
  disabled,
}: AddNewTaskProps) => {
  const theme = useTheme();
  const { modal, form, state, setters, options, loading, handlers } =
    useAddTaskForm({ prefilledAssignee, prefilledLead, prefilledProperty });
  const { evaluateTaskContent } = useTaskAiEvaluation();

  const { isMobile } = useScreenSize();

  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  // Check if user has permission to disable AI evaluation
  const canSkipEvaluation = userHasPermission(
    {
      name: PermissionsEnum.DISABLE_AI_EVALUATION,
      level: PermissionLevel.READ,
    },
    currentUserPermissions
  );

  const [evaluation, setEvaluation] = useState<TaskAiEvaluationResponse | null>(
    null
  );
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [confirmButtonText, setConfirmButtonText] = useState(
    canSkipEvaluation ? "Create" : "Evaluate"
  );
  const [taskReadyForEvaluation, setTaskReadyForEvaluation] = useState(false);
  const [lastEvaluatedTitle, setLastEvaluatedTitle] = useState<string>("");
  const [lastEvaluatedLinkedEntities, setLastEvaluatedLinkedEntities] =
    useState<string>("");

  // Watch all required fields using useWatch
  const title = useWatch({
    control: form.control,
    name: "title",
  });

  const type = useWatch({
    control: form.control,
    name: "type",
  });

  const userUId = useWatch({
    control: form.control,
    name: "userUId",
  });

  const dueDate = useWatch({
    control: form.control,
    name: "dueDate",
  });

  const selectedTime = useWatch({
    control: form.control,
    name: "selectedTime",
  });

  const linkedEntities = useWatch({
    control: form.control,
    name: "linkedEntities",
  });

  // Watch for changes and determine if task is ready for evaluation
  useEffect(() => {
    // If user can skip evaluation, don't need to track evaluation readiness
    if (canSkipEvaluation) {
      return;
    }

    // Check if all required fields are filled and no validation errors
    const allFieldsFilled =
      !!title &&
      title.trim() !== "" &&
      !!userUId &&
      userUId.trim() !== "" &&
      !!dueDate &&
      dueDate.trim() !== "" &&
      !!selectedTime &&
      selectedTime.trim() !== "";

    const hasValidationErrors = Object.keys(form.errors).length > 0;

    // Check if title has changed from last evaluation
    const titleHasChanged = title !== lastEvaluatedTitle;

    // Check if linked entities have changed from last evaluation
    // Handle both the value change and the case where linkedEntities might be undefined/null
    const currentLinkedEntitiesString = JSON.stringify(linkedEntities || []);
    const linkedEntitiesHaveChanged =
      lastEvaluatedLinkedEntities !== "" && // Only check if we've evaluated before
      currentLinkedEntitiesString !== lastEvaluatedLinkedEntities;

    // Check if any evaluation-relevant field has changed
    const hasRelevantChanges = titleHasChanged || linkedEntitiesHaveChanged;

    // Check if last evaluation was "Not Actionable"
    const lastEvaluationFailed =
      evaluation &&
      evaluation.classification !== "Actionable" &&
      evaluation.classification !== "Can Be Better";

    // Disable evaluate button if:
    // - Not all fields filled OR has validation errors
    const shouldDisableEvaluate =
      !allFieldsFilled ||
      hasValidationErrors ||
      (!hasRelevantChanges && lastEvaluationFailed);

    setTaskReadyForEvaluation(!shouldDisableEvaluate);

    // Reset button text and evaluation when form fields change after evaluation
    if (confirmButtonText === "Create" && hasRelevantChanges) {
      setConfirmButtonText("Evaluate");
      setEvaluation(null);
      setLastEvaluatedTitle("");
      setLastEvaluatedLinkedEntities("");
    }
  }, [
    title,
    type,
    userUId,
    dueDate,
    selectedTime,
    linkedEntities,
    form.errors,
    confirmButtonText,
    lastEvaluatedTitle,
    lastEvaluatedLinkedEntities,
    evaluation,
    canSkipEvaluation,
  ]);

  // Evaluate task function
  const evaluateTask = async (): Promise<boolean> => {
    if (!title || title.trim() === "") {
      return false;
    }

    setIsEvaluating(true);
    try {
      const linkedEntities = form.getValues("linkedEntities") || [];
      const contextMessage = buildTaskContentWithContext(title, linkedEntities);

      const result = await evaluateTaskContent(contextMessage);
      if (result) {
        setEvaluation(result);
        // Store the evaluated title and linked entities
        setLastEvaluatedTitle(title);
        setLastEvaluatedLinkedEntities(JSON.stringify(linkedEntities));

        const isValid =
          result.classification === "Actionable" ||
          result.classification === "Can Be Better";

        if (!isValid) {
          showToast({
            type: ToastType.WARNING,
            message: "Please improve the task title based on the feedback",
            toastId: "taskEvaluationWarning",
          });
        }

        return isValid;
      }
      return false;
    } catch (error) {
      console.error("Task evaluation failed:", error);
      showToast({
        type: ToastType.ERROR,
        message: "Failed to evaluate task. Please try again.",
        toastId: "taskEvaluationError",
      });
      return false;
    } finally {
      setIsEvaluating(false);
    }
  };

  // Handle modal close - reset all states
  const handleCloseModal = () => {
    form.reset();
    setEvaluation(null);
    setConfirmButtonText(canSkipEvaluation ? "Create" : "Evaluate");
    setTaskReadyForEvaluation(false);
    setIsEvaluating(false);
    setLastEvaluatedTitle("");
    setLastEvaluatedLinkedEntities("");
    modal.setOpen(false);
  };

  // Handle confirm button click
  const handleConfirm = async () => {
    // If user has permission to skip evaluation, submit directly
    if (canSkipEvaluation) {
      form.handleSubmit(form.onSubmit)();
      return;
    }

    // Otherwise, follow the evaluation flow
    if (confirmButtonText === "Evaluate") {
      const isValid = await evaluateTask();
      if (isValid) {
        setConfirmButtonText("Create");
      }
    } else {
      form.handleSubmit(form.onSubmit)();
    }
  };

  return (
    <>
      <NwButton
        disabled={disabled}
        variant="tonal"
        onClick={() => modal.setOpen(true)}
        disableRipple={disableRipple}
        startIcon={<PlusIcon />}
        sx={{
          display: "flex",
          width: "fit-content",
          borderRadius: 2,
          height: "36px",
          whiteSpace: "nowrap",
          padding: theme.nawy.spacing.md,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: theme.nawy.colors.semantic.layer.accentLight,
          color: theme.nawy.colors.semantic.text.accent,
          border: `1px solid ${theme.nawy.colors.semantic.layer.accentLight}`,
          gap: 0,
          "& .MuiButton-startIcon": {
            marginRight: `4px !important`,
            marginLeft: "0 !important",
          },
          "&:hover": {
            backgroundColor: theme.nawy.colors.semantic.layer.accentLight,
            opacity: 0.9,
          },
        }}
      >
        New Task
      </NwButton>

      <ResponsiveEditDialog
        open={modal.open}
        setOpen={modal.setOpen}
        width={750}
        header="Add Task"
        isLoading={form.isLoading || isEvaluating}
        handleConfirm={handleConfirm}
        confirmText={confirmButtonText}
        cancelText="Discard"
        showCancelButton={false}
        clearButton={
          <CancelButton
            onClick={handleCloseModal}
            disabled={form.isSubmitting || isEvaluating}
          />
        }
        handleCloseModal={handleCloseModal}
        disabledConfirmButton={
          canSkipEvaluation
            ? !!form.errors.title // Only check form errors if can skip evaluation
            : confirmButtonText === "Evaluate"
            ? !taskReadyForEvaluation
            : !!form.errors.title ||
              (evaluation !== null &&
                evaluation.classification !== "Actionable" &&
                evaluation.classification !== "Can Be Better")
        }
        applyButtonSx={{
          backgroundColor: theme.nawy.colors.semantic.layer.accent,
          color: theme.nawy.colors.semantic.text.onColor,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            paddingBottom: isMobile ? "90px" : 0,
          }}
        >
          {/* Task Title */}
          <Controller
            name="title"
            control={form.control}
            rules={{ required: "Title is required" }}
            render={({ field }) => {
              const hasEvaluationError =
                evaluation &&
                evaluation.classification !== "Actionable" &&
                evaluation.classification !== "Can Be Better";
              const hasError = !!form.errors.title || !!hasEvaluationError;

              return (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CustomLabel
                      sx={{
                        ...nawyTheme.nawy.typography.english.variants.body.xs
                          .regular,
                        color: nawyTheme.nawy.colors.semantic.text.secondary,
                      }}
                    >
                      Task
                    </CustomLabel>
                    {isEvaluating && <CircularProgress size={16} />}
                  </Box>
                  <TextField
                    placeholder="Task name"
                    error={hasError}
                    helperText={form.errors.title?.message as string}
                    fullWidth
                    size="small"
                    sx={{
                      width: "100%",
                      "& > .MuiBox-root:first-of-type": {
                        display: "none",
                      },
                      "& .MuiInputBase-root": {
                        height: "36px",
                        borderRadius: nawyTheme.nawy.radius.lg,
                        "& .MuiInputBase-input": {
                          fontSize: "1.0rem",
                          padding: "8px 14px",
                        },
                      },
                    }}
                    className="scroll-safe-input"
                    {...field}
                  />
                  {evaluation && (
                    <TaskAiEvaluationDisplay
                      evaluation={evaluation}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              );
            }}
          />

          <TaskFormFields
            control={form.control}
            errors={form.errors}
            state={state}
            setters={setters}
            options={options}
            loading={loading}
            handlers={handlers}
            disableAssignee={disableAssignee}
            disableLead={disableLead}
            disableProperty={disableProperty}
          />
        </Box>
      </ResponsiveEditDialog>
    </>
  );
};

export default AddNewTask;
